import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { GeneratorConfig, GeneratorUILabels, StandardsBehavior, AttachmentConfig } from "@/types/generator";
import { DEFAULT_UI_LABELS, DEFAULT_ATTACHMENT_CONFIG } from "@/types/generator";

interface UseGeneratorConfigResult {
  config: GeneratorConfig | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch generator configuration from the database
 * Falls back to default SWMS config if not found
 */
export function useGeneratorConfig(slug: string | null, options?: { allowInactive?: boolean }): UseGeneratorConfigResult {
  const [config, setConfig] = useState<GeneratorConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const allowInactive = options?.allowInactive ?? false;

  useEffect(() => {
    // If the generator slug isn't resolved yet, keep a neutral loading state.
    // This prevents briefly fetching/rendering the default (SWMS) generator.
    if (!slug) {
      setConfig(null);
      setIsLoading(true);
      setError(null);
      return;
    }

    async function fetchConfig() {
      setIsLoading(true);
      setError(null);

      try {
        // When allowInactive is true (dev override), skip the domain join and active filter
        let query;
        if (allowInactive) {
          query = supabase
            .from("generators")
            .select(`
              id,
              slug,
              acronym,
              display_name,
              full_name,
              jurisdiction_level,
              requires_project_reference,
              requires_standard_reference,
              requires_contract_reference,
              supported_countries,
              supported_export_formats,
              placeholder,
              hero_heading,
              hero_subheading,
              disclaimer,
              example_prompts,
              meta_title,
              meta_description,
              og_image_path,
              keywords,
              structured_data,
              favicon_path,
              ui_labels,
              standards_behavior,
              use_generic_pipeline,
              analysis_domain,
              about_what_is,
              about_how_it_works,
              success_marketing_heading1,
              success_marketing_heading2,
              success_marketing_copy,
              success_marketing_image,
              success_cta_label,
              success_cta_url,
              product_code,
              attachment_config,
              generator_domains(domain, is_primary),
              generator_validation_rules(max_prompt_length)
            `)
            .eq("slug", slug)
            .single();
        } else {
          query = supabase
            .from("generators")
            .select(`
              id,
              slug,
              acronym,
              display_name,
              full_name,
              jurisdiction_level,
              requires_project_reference,
              requires_standard_reference,
              requires_contract_reference,
              supported_countries,
              supported_export_formats,
              placeholder,
              hero_heading,
              hero_subheading,
              disclaimer,
              example_prompts,
              meta_title,
              meta_description,
              og_image_path,
              keywords,
              structured_data,
              favicon_path,
              ui_labels,
              standards_behavior,
              use_generic_pipeline,
              analysis_domain,
              about_what_is,
              about_how_it_works,
              success_marketing_heading1,
              success_marketing_heading2,
              success_marketing_copy,
              success_marketing_image,
              success_cta_label,
              success_cta_url,
              product_code,
              attachment_config,
              generator_domains!inner(domain),
              generator_validation_rules(max_prompt_length)
            `)
            .eq("slug", slug)
            .eq("is_active", true)
            .eq("generator_domains.is_primary", true)
            .single();
        }

        const { data, error: fetchError } = await query;

        if (fetchError) {
          console.error("Error fetching generator config:", fetchError);
          // Fall back to SWMS default if fetch fails
          if (slug === "swms") {
            setConfig(getSwmsDefault());
          } else {
            setError(`Generator "${slug}" not found`);
          }
          return;
        }

        if (data) {
          // Parse example_prompts from JSON
          const examplePrompts = Array.isArray(data.example_prompts) 
            ? data.example_prompts.map((p: unknown) => 
                typeof p === 'string' ? p : (p as Record<string, unknown>)?.text as string
              ).filter(Boolean) as string[]
            : [];

          setConfig({
            id: data.id,
            slug: data.slug,
            acronym: data.acronym,
            display_name: data.display_name,
            full_name: data.full_name,
            jurisdiction_level: data.jurisdiction_level as "state" | "country" | "none",
            requires_project_reference: data.requires_project_reference,
            requires_standard_reference: data.requires_standard_reference,
            requires_contract_reference: data.requires_contract_reference,
            supported_countries: data.supported_countries || [],
            supported_export_formats: data.supported_export_formats || ["docx"],
            placeholder: data.placeholder,
            hero_heading: data.hero_heading,
            hero_subheading: data.hero_subheading,
            disclaimer: data.disclaimer || `${data.display_name} Generator can make mistakes. Check draft documents before use.`,
            example_prompts: examplePrompts,
            // SEO fields
            meta_title: data.meta_title || undefined,
            meta_description: data.meta_description || undefined,
            og_image_path: data.og_image_path || undefined,
            keywords: data.keywords || undefined,
            structured_data: data.structured_data as Record<string, unknown> | undefined,
            favicon_path: data.favicon_path || undefined,
            // UI labels - merge with defaults
            ui_labels: {
              ...DEFAULT_UI_LABELS,
              ...(data.ui_labels as GeneratorUILabels || {}),
            },
            // Standards behavior
            standards_behavior: data.standards_behavior as StandardsBehavior | undefined,
            // Routing configuration
            use_generic_pipeline: data.use_generic_pipeline ?? true,
            // Analysis domain for activity analysis
            analysis_domain: (data.analysis_domain as "safety" | "quality" | "operational" | "environmental" | "none") || 'safety',
            // About modal content
            about_what_is: data.about_what_is || undefined,
            about_how_it_works: data.about_how_it_works || undefined,
            // Success modal marketing
            success_marketing_heading1: data.success_marketing_heading1 || undefined,
            success_marketing_heading2: data.success_marketing_heading2 || undefined,
            success_marketing_copy: data.success_marketing_copy || undefined,
            success_marketing_image: data.success_marketing_image || undefined,
            success_cta_label: data.success_cta_label || undefined,
            success_cta_url: data.success_cta_url || undefined,
            // Primary domain from generator_domains join (prefer is_primary=true, fallback to first)
            primary_domain: Array.isArray((data as any).generator_domains) && (data as any).generator_domains.length > 0
              ? ((data as any).generator_domains.find((d: any) => d.is_primary)?.domain || (data as any).generator_domains[0].domain)
              : undefined,
            // Product code
            product_code: data.product_code || undefined,
            // Attachments configuration (merge with defaults)
            attachment_config: {
              ...DEFAULT_ATTACHMENT_CONFIG,
              ...((data as any).attachment_config as Partial<AttachmentConfig> || {}),
            },
            // Prompt length limit from validation rules
            max_prompt_length: Array.isArray((data as any).generator_validation_rules) && (data as any).generator_validation_rules.length > 0
              ? (data as any).generator_validation_rules[0].max_prompt_length
              : undefined,
          });
        }
      } catch (err) {
        console.error("Failed to fetch generator config:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
        
        // Fall back to SWMS default
        if (slug === "swms") {
          setConfig(getSwmsDefault());
        }
      } finally {
        setIsLoading(false);
      }
    }

    fetchConfig();
  }, [slug]);

  return { config, isLoading, error };
}

/**
 * Hook to fetch all active generators
 */
export function useGeneratorList() {
  const [generators, setGenerators] = useState<GeneratorConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchGenerators() {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from("generators")
          .select(`
            id,
            slug,
            acronym,
            display_name,
            full_name,
            jurisdiction_level,
            requires_project_reference,
            requires_standard_reference,
            requires_contract_reference,
            supported_countries,
            supported_export_formats,
            placeholder,
            hero_heading,
            hero_subheading,
            disclaimer,
            example_prompts,
            sort_order
          `)
          .eq("is_active", true)
          .order("sort_order", { ascending: true });

        if (fetchError) {
          console.error("Error fetching generators:", fetchError);
          setError("Failed to load generators");
          return;
        }

        if (data) {
          setGenerators(
            data.map((g) => {
              const examplePrompts = Array.isArray(g.example_prompts) 
                ? g.example_prompts as string[]
                : [];

              return {
                id: g.id,
                slug: g.slug,
                acronym: g.acronym,
                display_name: g.display_name,
                full_name: g.full_name,
                jurisdiction_level: g.jurisdiction_level as "state" | "country" | "none",
                requires_project_reference: g.requires_project_reference,
                requires_standard_reference: g.requires_standard_reference,
                requires_contract_reference: g.requires_contract_reference,
                supported_countries: g.supported_countries || [],
                supported_export_formats: g.supported_export_formats || ["docx"],
                placeholder: g.placeholder,
                hero_heading: g.hero_heading,
                hero_subheading: g.hero_subheading,
                disclaimer: g.disclaimer || `${g.display_name} Generator can make mistakes. Check draft documents before use.`,
                example_prompts: examplePrompts,
              };
            })
          );
        }
      } catch (err) {
        console.error("Failed to fetch generators:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setIsLoading(false);
      }
    }

    fetchGenerators();
  }, []);

  return { generators, isLoading, error };
}

// Default SWMS config for fallback
function getSwmsDefault(): GeneratorConfig {
  return {
    id: "ef0acee6-2c17-4043-b84c-56c6a3fd0e9a",
    slug: "swms",
    acronym: "SWMS",
    display_name: "SWMS",
    full_name: "Safe Work Method Statement",
    jurisdiction_level: "state",
    requires_project_reference: false,
    requires_standard_reference: false,
    requires_contract_reference: false,
    supported_countries: ["Australia", "New Zealand"],
    supported_export_formats: ["docx"],
    placeholder: "Describe the work activity...",
    hero_heading: "SWMS Generator",
    hero_subheading: "Generate Safe Work Method Statements (SWMS) in seconds - free and unlimited.",
    disclaimer: "SWMS Generator can make mistakes. Check draft documents before use.",
    example_prompts: [
      "Operating a 6m electric scissor lift indoors to install overhead lighting in a warehouse.",
      "Excavating a 1.2m deep trench for laying new electrical conduits along a footpath.",
      "Using a 125mm angle grinder to cut 50x50 SHS steel sections in a fabrication workshop.",
    ],
    // SEO defaults
    meta_title: "SWMS Generator - Free Safe Work Method Statements",
    meta_description: "Generate professional Safe Work Method Statements (SWMS) in seconds. Free, unlimited, and compliant with Australian workplace safety requirements.",
    keywords: ["SWMS", "Safe Work Method Statement", "workplace safety", "risk assessment", "Australia", "construction safety"],
    favicon_path: "/favicon.png",
    // UI labels
    ui_labels: DEFAULT_UI_LABELS,
  };
}
